import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 transition-colors duration-200",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 transition-colors duration-200",
        outline:
          "border border-border bg-transparent text-foreground shadow-sm hover:bg-secondary hover:text-secondary-foreground transition-colors duration-200",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80 transition-colors duration-200",
        ghost:
          "text-foreground hover:bg-secondary hover:text-secondary-foreground transition-colors duration-200",
        link:
          "text-primary underline-offset-4 hover:underline transition-colors duration-200",
        /* Premium CTA - Primary emerald gradient */
        premium:
          "bg-primary text-primary-foreground font-semibold shadow-glow-primary hover:shadow-[0_0_40px_hsl(155_72%_42%/0.35)] hover:brightness-110 transition-all duration-300",
        /* Accent CTA - Amber gold */
        accent:
          "bg-accent text-accent-foreground font-semibold shadow-glow-accent hover:brightness-110 transition-all duration-300",
        /* Glass button for dark surfaces */
        glass:
          "bg-[hsl(210_20%_95%/0.08)] text-foreground border border-[hsl(210_20%_95%/0.12)] backdrop-blur-md hover:bg-[hsl(210_20%_95%/0.14)] transition-all duration-300",
        /* Outline primary - green border */
        "outline-primary":
          "border border-primary/40 text-primary bg-transparent hover:bg-primary/10 transition-all duration-300",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-12 rounded-xl px-8 text-base",
        xl: "h-14 rounded-xl px-10 text-base font-semibold",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
